import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Button,
  Autocomplete, TextField, Chip
} from '@mui/material';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import HeaderPimpinan from '../components/HeaderPimpinan';

const StatistikPresenter = () => {
  const [loading, setLoading] = useState(true);
  const [presenterStats, setPresenterStats] = useState([]);
  const [selectedPresenter, setSelectedPresenter] = useState('all');
  const [selectedCabang, setSelectedCabang] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [presenterList, setPresenterList] = useState([]);
  const [kantorList, setKantorList] = useState([]);

  // Helper function to parse Indonesian currency format
  const parseCurrency = (value) => {
    if (!value) return 0;
    const cleanValue = value.toString().replace(/\./g, '');
    return parseFloat(cleanValue) || 0;
  };

  const processPresenterStatistics = (presenterList, pendaftaranList, daftarUlangList = []) => {
    const stats = {};

    // Helper function untuk normalisasi nama
    const normalizeString = (str) => str?.trim().toLowerCase().replace(/\s+/g, ' ') || '';

    // Inisialisasi stats untuk setiap presenter
    presenterList.forEach(presenter => {
      const normalizedName = normalizeString(presenter.namaLengkap);
      stats[presenter.namaLengkap] = {
        namaPresenter: presenter.namaLengkap,
        normalizedName,
        alamat: presenter.alamat || 'Tidak Diketahui',
        totalPendaftaran: 0,
        totalPendaftaranIndividu: 0,
        totalPendaftaranKelompok: 0,
        totalDaftarUlang: 0,
        totalDaftarUlangIndividu: 0,
        totalDaftarUlangKelompok: 0,
        totalBiayaPendaftaran: 0,
        totalBiayaDaftarUlang: 0,
        pendaftaranDetail: [],
        daftarUlangDetail: []
      };
    });

    // Hitung pendaftaran per presenter dan per bulan
    pendaftaranList.forEach(pendaftaran => {
      const presenterNames = Array.isArray(pendaftaran.presenter) ? pendaftaran.presenter : [pendaftaran.presenter];
      const isKelompok = presenterNames.length > 1;

      presenterNames.forEach(presenterName => {
        if (presenterName && stats[presenterName]) {
          // Ekstrak informasi tanggal
          const tanggalDaftar = new Date(pendaftaran.tglDaftar);

          // Simpan detail pendaftaran
          stats[presenterName].pendaftaranDetail.push({
            ...pendaftaran,
            tanggalDaftar,
            isKelompok,
            biayaParsed: parseCurrency(pendaftaran.totalBiayaPendaftaran)
          });

          stats[presenterName].totalPendaftaran++;
          if (isKelompok) {
            stats[presenterName].totalPendaftaranKelompok++;
          } else {
            stats[presenterName].totalPendaftaranIndividu++;
          }
        }
      });
    });

    // Hitung daftar ulang per presenter dan per bulan
    // Buat map nomor pendaftaran ke presenter untuk fallback
    const pendaftaranToPresenter = {};
    pendaftaranList.forEach(pendaftaran => {
      if (pendaftaran.nomorPendaftaran && pendaftaran.presenter) {
        pendaftaranToPresenter[pendaftaran.nomorPendaftaran] = pendaftaran.presenter;
      }
    });

    daftarUlangList.forEach((daftarUlang) => {
      // Validasi: hanya hitung jika ada DU Tahap 1 atau DU Tahap 2 dengan tanggal yang terisi
      const hasDU1 = daftarUlang.duTahap1 && daftarUlang.tglDU1;
      const hasDU2 = daftarUlang.duTahap2 && daftarUlang.tglDU2;

      // Skip jika tidak memiliki data DU yang valid
      if (!hasDU1 && !hasDU2) {
        return;
      }

      // Cara 1: Gunakan field presenter langsung dari daftar ulang
      let presenterSource = daftarUlang.presenter;

      // Cara 2: Jika tidak ada presenter di daftar ulang, cari dari data pendaftaran
      if (!presenterSource && daftarUlang.nomorPendaftaran) {
        presenterSource = pendaftaranToPresenter[daftarUlang.nomorPendaftaran];
      }

      const presenterNames = Array.isArray(presenterSource) ? presenterSource : [presenterSource];
      const isKelompok = presenterNames.length > 1;

      presenterNames.forEach(presenterName => {
        // Cari presenter dengan exact match terlebih dahulu
        let matchedPresenterKey = null;
        if (presenterName && stats[presenterName]) {
          matchedPresenterKey = presenterName;
        } else if (presenterName) {
          // Jika tidak exact match, coba fuzzy match berdasarkan normalisasi nama
          const normalizedSearchName = normalizeString(presenterName);
          matchedPresenterKey = Object.keys(stats).find(key =>
            stats[key].normalizedName === normalizedSearchName
          );
        }

        if (matchedPresenterKey && stats[matchedPresenterKey]) {
          // Ekstrak informasi tanggal - prioritas tglDU1, fallback ke tglDU2
          let tanggalDU;
          if (daftarUlang.duTahap1 && daftarUlang.tglDU1) {
            tanggalDU = daftarUlang.tglDU1;
          } else if (daftarUlang.duTahap2 && daftarUlang.tglDU2) {
            tanggalDU = daftarUlang.tglDU2;
          } else {
            tanggalDU = daftarUlang.timestamp?.toDate?.() || new Date();
          }

          const daftarUlangDate = new Date(tanggalDU);

          // Simpan detail daftar ulang
          let totalBiayaDU = 0;
          if (daftarUlang.duTahap1) totalBiayaDU += parseCurrency(daftarUlang.duTahap1);
          if (daftarUlang.duTahap2) totalBiayaDU += parseCurrency(daftarUlang.duTahap2);

          stats[matchedPresenterKey].daftarUlangDetail.push({
            ...daftarUlang,
            tanggalDaftarUlang: daftarUlangDate,
            isKelompok,
            biayaParsed: totalBiayaDU
          });

          stats[matchedPresenterKey].totalDaftarUlang++;
          if (isKelompok) {
            stats[matchedPresenterKey].totalDaftarUlangKelompok++;
          } else {
            stats[matchedPresenterKey].totalDaftarUlangIndividu++;
          }
        }
      });
    });

    setPresenterStats(Object.values(stats));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch data kantor
        const kantorSnapshot = await getDocs(collection(db, 'kantor'));
        const kantorData = kantorSnapshot.docs.map(doc => doc.data().namaKantor);
        setKantorList(kantorData);

        // Fetch data presenter
        const presenterSnapshot = await getDocs(collection(db, 'presenter'));
        const presenterData = presenterSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPresenterList(presenterData);

        // Fetch data pendaftaran
        const pendaftaranSnapshot = await getDocs(collection(db, 'pendaftaran_siswa'));
        const pendaftaranList = pendaftaranSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Fetch data daftar ulang
        const daftarUlangSnapshot = await getDocs(collection(db, 'daftar_ulang'));
        const daftarUlangList = daftarUlangSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Proses statistik berdasarkan presenter dengan data daftar ulang
        processPresenterStatistics(presenterData, pendaftaranList, daftarUlangList);

      } catch (error) {
        // Error handling sudah ditangani di UI dengan loading state
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getFilteredData = () => {
    let filteredStats = presenterStats;

    if (selectedPresenter !== 'all') {
      filteredStats = filteredStats.filter(stat => stat.namaPresenter === selectedPresenter);
    }

    if (selectedCabang !== 'all') {
      filteredStats = filteredStats.filter(stat => stat.alamat === selectedCabang);
    }

    const start = startDate ? new Date(startDate).setHours(0, 0, 0, 0) : null;
    const end = endDate ? new Date(endDate).setHours(23, 59, 59, 999) : null;

    filteredStats = filteredStats.map(presenter => {
      const filteredPendaftaranDetail = presenter.pendaftaranDetail.filter(p => {
        if (!start && !end) return true;
        const pDate = new Date(p.tglDaftar).getTime();
        if (start && end) return pDate >= start && pDate <= end;
        if (start) return pDate >= start;
        if (end) return pDate <= end;
        return true;
      });

      const filteredDaftarUlangDetail = presenter.daftarUlangDetail.filter(du => {
        if (!start && !end) return true;
        let dateToUse;
        if (du.duTahap1 && du.tglDU1) dateToUse = du.tglDU1;
        else if (du.duTahap2 && du.tglDU2) dateToUse = du.tglDU2;
        else return false;

        const dDate = new Date(dateToUse).getTime();
        if (start && end) return dDate >= start && dDate <= end;
        if (start) return dDate >= start;
        if (end) return dDate <= end;
        return true;
      });

      const totalBiayaPendaftaran = filteredPendaftaranDetail.reduce((sum, p) => sum + p.biayaParsed, 0);
      const totalBiayaDaftarUlang = filteredDaftarUlangDetail.reduce((sum, du) => sum + du.biayaParsed, 0);

      return {
        ...presenter,
        totalPendaftaran: filteredPendaftaranDetail.length,
        totalPendaftaranIndividu: filteredPendaftaranDetail.filter(p => !p.isKelompok).length,
        totalPendaftaranKelompok: filteredPendaftaranDetail.filter(p => p.isKelompok).length,
        totalDaftarUlang: filteredDaftarUlangDetail.length,
        totalDaftarUlangIndividu: filteredDaftarUlangDetail.filter(p => !p.isKelompok).length,
        totalDaftarUlangKelompok: filteredDaftarUlangDetail.filter(p => p.isKelompok).length,
        totalBiayaPendaftaran,
        totalBiayaDaftarUlang,
        pendaftaranDetail: filteredPendaftaranDetail,
        daftarUlangDetail: filteredDaftarUlangDetail
      };
    });

    return filteredStats;
  };

  const getBranchComparisonData = () => {
    const data = getFilteredData();
    const branchStats = {};

    data.forEach(presenter => {
      const branchName = presenter.alamat || 'Tidak Diketahui';
      if (!branchStats[branchName]) {
        branchStats[branchName] = {
          namaCabang: branchName,
          jumlahPresenter: 0,
          uniquePendaftaran: new Map(),
          uniqueDaftarUlang: new Map()
        };
      }

      branchStats[branchName].jumlahPresenter++;

      presenter.pendaftaranDetail.forEach(p => {
        if (p.nomorPendaftaran) {
          branchStats[branchName].uniquePendaftaran.set(p.nomorPendaftaran, p);
        }
      });

      presenter.daftarUlangDetail.forEach(du => {
        if (du.nomorPendaftaran) {
          branchStats[branchName].uniqueDaftarUlang.set(du.nomorPendaftaran, du);
        }
      });
    });

    const result = Object.values(branchStats).map(branch => {
      const pendaftaranArr = Array.from(branch.uniquePendaftaran.values());
      const daftarUlangArr = Array.from(branch.uniqueDaftarUlang.values());

      return {
        namaCabang: branch.namaCabang,
        jumlahPresenter: branch.jumlahPresenter,
        totalPendaftaran: pendaftaranArr.length,
        totalDaftarUlang: daftarUlangArr.length,
        totalBiayaPendaftaran: pendaftaranArr.reduce((sum, p) => sum + p.biayaParsed, 0),
        totalBiayaDaftarUlang: daftarUlangArr.reduce((sum, du) => sum + du.biayaParsed, 0)
      };
    });

    return result.sort((a, b) => b.totalBiayaPendaftaran - a.totalBiayaPendaftaran);
  };

  const getUniquePendaftaran = () => {
    const unique = new Map();
    getFilteredData().forEach(presenter => {
      presenter.pendaftaranDetail.forEach(p => {
        if (p.nomorPendaftaran) unique.set(p.nomorPendaftaran, p);
      });
    });
    return Array.from(unique.values());
  };

  const getUniqueDaftarUlang = () => {
    const unique = new Map();
    getFilteredData().forEach(presenter => {
      presenter.daftarUlangDetail.forEach(du => {
        if (du.nomorPendaftaran) unique.set(du.nomorPendaftaran, du);
      });
    });
    return Array.from(unique.values());
  };

  const getTotalPendaftaran = () => getUniquePendaftaran().length;
  const getTotalBiayaPendaftaran = () => getUniquePendaftaran().reduce((sum, p) => sum + p.biayaParsed, 0);

  const getTotalDaftarUlang = () => getUniqueDaftarUlang().length;
  const getTotalBiayaDaftarUlang = () => getUniqueDaftarUlang().reduce((sum, du) => sum + du.biayaParsed, 0);

  const handleResetFilters = () => {
    setSelectedPresenter('all');
    setSelectedCabang('all');
    setStartDate('');
    setEndDate('');
  };

  if (loading) {
    return (
      <>
        <HeaderPimpinan />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  return (
    <>
      <HeaderPimpinan />
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Statistik Perolehan Presenter
        </Typography>

        {/* Info Period Filter */}
        {(startDate || endDate) && (
          <Typography variant="h6" color="primary" gutterBottom>
            Periode: {startDate ? startDate : 'Awal'} s/d {endDate ? endDate : 'Sekarang'}
          </Typography>
        )}

        {/* Filter */}
        <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Autocomplete
            options={['Semua Presenter', ...presenterList.map(p => p.namaLengkap)]}
            value={selectedPresenter === 'all' ? 'Semua Presenter' : selectedPresenter}
            onChange={(event, newValue) => {
              setSelectedPresenter(newValue === 'Semua Presenter' || newValue === null ? 'all' : newValue);
            }}
            renderInput={(params) => <TextField {...params} label="Filter Presenter" size="small" />}
            sx={{ minWidth: 250, flex: 1 }}
          />

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter Cabang</InputLabel>
            <Select
              value={selectedCabang}
              label="Filter Cabang"
              onChange={(e) => setSelectedCabang(e.target.value)}
            >
              <MenuItem value="all">Semua Cabang</MenuItem>
              {kantorList.map(kantor => (
                <MenuItem key={kantor} value={kantor}>{kantor}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Tanggal Mulai"
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Tanggal Akhir"
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          {/* Reset Button */}
          {(selectedPresenter !== 'all' || selectedCabang !== 'all' || startDate || endDate) && (
            <Button variant="outlined" onClick={handleResetFilters} size="small">
              Reset Filter
            </Button>
          )}
        </Paper>

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ height: '100%', bgcolor: '#f8f9fa' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Presenter
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {getFilteredData().length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography color="primary" gutterBottom variant="body2">
                  Pendaftaran Baru
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {getTotalPendaftaran()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography color="success.main" gutterBottom variant="body2">
                  Daftar Ulang
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {getTotalDaftarUlang()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography color="warning.main" gutterBottom variant="body2">
                  Biaya Pendaftaran
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  Rp {getTotalBiayaPendaftaran().toLocaleString('id-ID')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography color="info.main" gutterBottom variant="body2">
                  Biaya Daftar Ulang
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  Rp {getTotalBiayaDaftarUlang().toLocaleString('id-ID')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Branch Comparison Table */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Perbandingan Antar Cabang
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell>Nama Cabang</TableCell>
                  <TableCell align="center">Jumlah Presenter</TableCell>
                  <TableCell align="center">Total Pendaftaran</TableCell>
                  <TableCell align="center">Total Daftar Ulang</TableCell>
                  <TableCell align="right">Total Biaya Pendaftaran</TableCell>
                  <TableCell align="right">Total Biaya Daftar Ulang</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getBranchComparisonData().map((branch, index) => (
                  <TableRow key={index}>
                    <TableCell><Typography fontWeight="bold">{branch.namaCabang}</Typography></TableCell>
                    <TableCell align="center">
                      <Chip label={branch.jumlahPresenter} size="small" />
                    </TableCell>
                    <TableCell align="center">{branch.totalPendaftaran}</TableCell>
                    <TableCell align="center">{branch.totalDaftarUlang}</TableCell>
                    <TableCell align="right" sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                      Rp {branch.totalBiayaPendaftaran.toLocaleString('id-ID')}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'info.main', fontWeight: 'bold' }}>
                      Rp {branch.totalBiayaDaftarUlang.toLocaleString('id-ID')}
                    </TableCell>
                  </TableRow>
                ))}
                {getBranchComparisonData().length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">Tidak ada data cabang</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Charts */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Summary Chart - Pendaftaran per Presenter */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Perolehan Pendaftaran dan Daftar Ulang per Presenter
              </Typography>
              <Box sx={{ mt: 2 }}>
                {getFilteredData()
                  .sort((a, b) => b.totalPendaftaran - a.totalPendaftaran)
                  .slice(0, 10) // Top 10 presenter
                  .map((presenter, index) => (
                    <Box key={index} sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" fontWeight="bold">{presenter.namaPresenter}</Typography>
                        <Typography variant="body2">
                          {presenter.totalPendaftaran} pendaftaran | {presenter.totalDaftarUlang} daftar ulang
                        </Typography>
                      </Box>

                      {/* Bar untuk Pendaftaran */}
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">Pendaftaran</Typography>
                        <Box sx={{
                          width: '100%',
                          height: 6,
                          backgroundColor: '#f0f0f0',
                          borderRadius: 3,
                          overflow: 'hidden'
                        }}>
                          <Box sx={{
                            width: `${Math.max((presenter.totalPendaftaran / Math.max(...getFilteredData().map(p => p.totalPendaftaran), 1)) * 100, 3)}%`,
                            height: '100%',
                            backgroundColor: '#1976d2',
                            borderRadius: 3
                          }} />
                        </Box>
                      </Box>

                      {/* Bar untuk Daftar Ulang */}
                      <Box>
                        <Typography variant="caption" color="text.secondary">Daftar Ulang</Typography>
                        <Box sx={{
                          width: '100%',
                          height: 6,
                          backgroundColor: '#f0f0f0',
                          borderRadius: 3,
                          overflow: 'hidden'
                        }}>
                          <Box sx={{
                            width: `${Math.max((presenter.totalDaftarUlang / Math.max(...getFilteredData().map(p => p.totalPendaftaran), 1)) * 100, 2)}%`,
                            height: '100%',
                            backgroundColor: '#4caf50',
                            borderRadius: 3
                          }} />
                        </Box>
                      </Box>
                    </Box>
                  ))}
              </Box>
            </Paper>
          </Grid>

          {/* Summary Stats */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Top 5 Presenter
              </Typography>
              <Box sx={{ mt: 2 }}>
                {getFilteredData()
                  .sort((a, b) => b.totalPendaftaran - a.totalPendaftaran)
                  .slice(0, 5)
                  .map((presenter, index) => {
                    return (
                      <Box key={index} sx={{
                        p: 2,
                        mb: 2,
                        backgroundColor: index % 2 === 0 ? '#f8f9fa' : 'white',
                        borderRadius: 2,
                        border: '1px solid #e0e0e0'
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              #{index + 1} {presenter.namaPresenter}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {presenter.alamat}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                          <Box sx={{ textAlign: 'center', flex: 1 }}>
                            <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                              {presenter.totalPendaftaran}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Pendaftaran
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center', flex: 1 }}>
                            <Typography variant="h6" color="success.main" sx={{ fontWeight: 'bold' }}>
                              {presenter.totalDaftarUlang}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Daftar Ulang
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Detail Table */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Detail Statistik per Presenter
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>No</TableCell>
                  <TableCell>Nama Presenter</TableCell>
                  <TableCell>Cabang</TableCell>
                  <TableCell align="center">Pend. Individu</TableCell>
                  <TableCell align="center">Pend. Kelompok</TableCell>
                  <TableCell align="center">DU Individu</TableCell>
                  <TableCell align="center">DU Kelompok</TableCell>
                  <TableCell align="center">Total Pend.</TableCell>
                  <TableCell align="center">Total DU</TableCell>
                  <TableCell align="right">Biaya Pend.</TableCell>
                  <TableCell align="right">Biaya DU</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredData()
                  .sort((a, b) => b.totalBiayaPendaftaran - a.totalBiayaPendaftaran)
                  .map((presenter, index) => {
                    return (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{presenter.namaPresenter}</TableCell>
                        <TableCell>{presenter.alamat}</TableCell>
                        <TableCell align="center">{presenter.totalPendaftaranIndividu}</TableCell>
                        <TableCell align="center">{presenter.totalPendaftaranKelompok}</TableCell>
                        <TableCell align="center">{presenter.totalDaftarUlangIndividu}</TableCell>
                        <TableCell align="center">{presenter.totalDaftarUlangKelompok}</TableCell>
                        <TableCell align="center"><Chip size="small" label={presenter.totalPendaftaran} color="primary" variant="outlined" /></TableCell>
                        <TableCell align="center"><Chip size="small" label={presenter.totalDaftarUlang} color="success" variant="outlined" /></TableCell>
                        <TableCell align="right">Rp {presenter.totalBiayaPendaftaran.toLocaleString('id-ID')}</TableCell>
                        <TableCell align="right">Rp {presenter.totalBiayaDaftarUlang.toLocaleString('id-ID')}</TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Keterangan Kalkulasi */}
          {(() => {
            const currentData = getFilteredData();
            const totalIndividu = currentData.reduce((sum, p) => sum + p.totalPendaftaranIndividu, 0);
            const totalKelompok = currentData.reduce((sum, p) => sum + p.totalPendaftaranKelompok, 0);
            const estimasiTotalPendaftaran = totalIndividu + (totalKelompok / 2);

            const totalDUIndividu = currentData.reduce((sum, p) => sum + p.totalDaftarUlangIndividu, 0);
            const totalDUKelompok = currentData.reduce((sum, p) => sum + p.totalDaftarUlangKelompok, 0);
            const estimasiTotalDU = totalDUIndividu + (totalDUKelompok / 2);

            return (
              <Box sx={{ mt: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
                  ℹ️ Keterangan Kalkulasi Statistik
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 1 }}>
                  <strong>Total di Card Ringkasan:</strong> Menghitung jumlah formulir pendaftaran/daftar ulang yang unik. Jika 1 formulir adalah pendaftaran kelompok (memiliki lebih dari 1 presenter), maka pada card di atas akan tetap dihitung sebagai <strong>1 pendaftaran/daftar ulang</strong>.
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 2 }}>
                  <strong>Detail Tabel per Presenter:</strong> Menghitung pencapaian untuk masing-masing individu presenter. Jika 1 pendaftaran kelompok dilakukan oleh 3 presenter bersama, maka setiap presenter di tabel ini akan mendapatkan masing-masing <strong>1 pencapaian</strong>. Oleh karena itu, total jika dijumlahkan dari tabel ini mungkin lebih besar daripada total unik pada card ringkasan.
                </Typography>

                <Box sx={{ p: 2, bgcolor: 'rgba(25, 118, 210, 0.08)', borderRadius: 1, border: '1px solid rgba(25, 118, 210, 0.2)' }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
                    📈 Kalkulasi Real-time dari Tabel
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Pendaftaran:</Typography>
                      <Typography variant="body2">• Total Pend. Individu: <strong>{totalIndividu}</strong></Typography>
                      <Typography variant="body2">• Total Pend. Kelompok: <strong>{totalKelompok}</strong></Typography>
                      <Typography variant="body2" sx={{ mt: 1, p: 1, bgcolor: '#fff', borderRadius: 1, border: '1px dashed #1976d2' }}>
                        <strong>Kalkulasi:</strong> {totalIndividu} + ({totalKelompok} / 2) = <strong>{estimasiTotalPendaftaran}</strong>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Daftar Ulang:</Typography>
                      <Typography variant="body2">• Total DU Individu: <strong>{totalDUIndividu}</strong></Typography>
                      <Typography variant="body2">• Total DU Kelompok: <strong>{totalDUKelompok}</strong></Typography>
                      <Typography variant="body2" sx={{ mt: 1, p: 1, bgcolor: '#fff', borderRadius: 1, border: '1px dashed #1976d2' }}>
                        <strong>Kalkulasi:</strong> {totalDUIndividu} + ({totalDUKelompok} / 2) = <strong>{estimasiTotalDU}</strong>
                      </Typography>
                    </Grid>
                  </Grid>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                    *Hasil kalkulasi ini menunjukkan pendekatan untuk menyamakan total yang ada di detail per presenter dengan total unik di card ringkasan, dengan asumsi 1 kelompok terdiri dari 2 presenter.
                  </Typography>
                </Box>
              </Box>
            );
          })()}
        </Paper>
      </Box>
    </>
  );
};

export default StatistikPresenter;
